import React from "react";
import { SchedulesOverview, ScheduleDetails } from ".";
import { Loading, DisplayBox } from "../../components";
import { ApiHelper, ArrayHelper, ScheduleTimeInterface, ScheduleInterface, ScreenInterface, SchedulePlaylistInterface, PlaylistInterface, OverlayInterface } from "../../helpers";
import {Dropdown} from "react-bootstrap";
import { Modal, Button, Form } from "react-bootstrap";
import { Row, Col, FormGroup, FormLabel, FormControl } from 'react-bootstrap';

interface CopiedScheduleTime {
  id?: number;
  scheduleId: number;
  dayOfWeek?: number;
  startHour?: number;
  startMinute?: number;
  endHour?: number;
  endMinute?: number;
  name?: number;

}

interface CopiedSchedulePlaylist {
  id?: number;
  scheduleId?: number;
  playlistId?: number;
  weight?: number;
}

interface CopiedOverlay{
  id?: number;
  scheduleId?: number;
  templateId?: number;
  position?: string;
}

interface Props {
  screen: ScreenInterface,
}

interface Schedule {
  id: string;
}

interface CopiedSchedule {
  id?: number;
  name?: string;
  screenId: number;
  summary?: string;

}


export const Schedules: React.FC<Props> = props => {

  const [schedules, setSchedules] = React.useState<ScheduleInterface[]>(null);
  const [scheduleTimes, setScheduleTimes] = React.useState<ScheduleTimeInterface[]>(null);
  const [selectedSchedule, setSelectedSchedule] = React.useState<ScheduleInterface>(null);
  const [schedulePlaylists, setSchedulePlaylists] = React.useState<SchedulePlaylistInterface[]>([]);
  const [playlists, setPlaylists] = React.useState<PlaylistInterface[]>([]);
  const [allScreens, setAllScreens] = React.useState<ScreenInterface[]>([]);
  const [showModal, setShowModal] = React.useState(false);
  const [selectedScreenSchedules, setSelectedScreenSchedules] = React.useState<ScheduleInterface[]>([]);
  const [selectedScheduleFromModal, setSelectedScheduleFromModal] = React.useState<ScheduleInterface>(null);
  const [isScheduleSelected, setIsScheduleSelected] = React.useState(false);
  const [copiedScheduleTimes, setCopiedScheduleTimes] = React.useState<CopiedScheduleTime[]>([]);
  const [copiedSchedulePlaylist, setCopiedSchedulePlaylist] = React.useState<CopiedSchedulePlaylist[]>([]);
  const [copiedOverlay, setCopiedOverlay] = React.useState<CopiedOverlay[]>([]);

  const resetModalState = () => {
    setSelectedScheduleFromModal(null);
    setSelectedScreenSchedules([]);
  };

  const init = () => {
    loadData();
  }

  const loadData = async () => {
    setSelectedSchedule(null);

    ApiHelper.get<PlaylistInterface[]>("/playlists/available")
      .then(p => setPlaylists(p || []))
      .catch(() => { /* playlists load failed */ });

    if (props.screen?.id > 0) {
      ApiHelper.get<SchedulePlaylistInterface[]>("/schedulePlaylists/screen/" + props.screen.id)
        .then(sp => setSchedulePlaylists(sp || []))
        .catch(() => { /* schedule playlists load failed */ });
      const scheduleData = await ApiHelper.get<Schedule[]>("/schedules/screen/" + props.screen?.id) || [];
      const convertedScheduleData: ScheduleInterface[] = scheduleData.map(schedule => ({
        ...schedule,
        id: Number(schedule.id),
      }));
      setSchedules(convertedScheduleData);

      if (scheduleData.length > 0) {
        const scheduleIds = scheduleData.map((s) => s.id).join(",");
        const scheduleTimeData = await ApiHelper.get<ScheduleTimeInterface[]>(`/scheduleTimes/scheduleIds?scheduleIds=${scheduleIds}`) || [];
        setScheduleTimes(scheduleTimeData);
      } else {
        setScheduleTimes([]);
      }
    } else {
      setSchedules([]);
      setScheduleTimes([]);
    }
  };


  const fetchAllScreens = async () => {
    const screens = await ApiHelper.get<ScreenInterface[]>("/screens") || [];
    const filteredScreens: ScreenInterface[]= [];
    for (const s of screens) {
      const schedules = await getScreenSchedules(s.id);
      if (!!schedules.length) {
        filteredScreens.push(s);
      }
    }
    setAllScreens(filteredScreens);
  };

  const handleCopyFromScreen = () => {
    setShowModal(true);
    fetchAllScreens();
  };

  const getScreenSchedules = async (screenId: number) => {
    return await ApiHelper.get<ScheduleInterface[]>(`/schedules/screen/${screenId}`) || [];
  };

  const handleScreenSelect = async (screenId: number) => {
    const schedules = await getScreenSchedules(screenId);
    setSelectedScreenSchedules(schedules);
  };



  const handleScheduleSelect = async (schedule: ScheduleInterface) => {
    const copiedSchedule: CopiedSchedule = {
      ...schedule,
      screenId: props.screen?.id,
      id: undefined,
      name: schedule.name || "Default Schedule Name",
    };
    setSelectedScheduleFromModal(copiedSchedule);


    const scheduleTimeData = await ApiHelper.get<ScheduleTimeInterface[]>(`/scheduleTimes/scheduleIds?scheduleIds=${schedule.id}`) || [];
    const copiedTimes: CopiedScheduleTime[] = scheduleTimeData.map((st: ScheduleTimeInterface) => ({
      scheduleId: copiedSchedule.screenId,
      dayOfWeek: st.dayOfWeek,
      startHour: st.startHour,
      startMinute: st.startMinute,
      endHour: st.endHour,
      endMinute: st.endMinute,
    }));
    setCopiedScheduleTimes(copiedTimes);

    const playlistsData = await ApiHelper.get<SchedulePlaylistInterface[]>(`/schedulePlaylists/schedule/${schedule.id}`) || [];
    const copiedPlaylist: CopiedSchedulePlaylist[] = playlistsData.map((sp: SchedulePlaylistInterface) => ({
      scheduleId: copiedSchedule.screenId,
      playlistId: sp.playlistId,
      weight: sp.weight,

    }))
    setCopiedSchedulePlaylist(copiedPlaylist);

    const overlayData = await ApiHelper.get<OverlayInterface[]>(`/overlays/schedule/${schedule.id}`) || [];
    const copiedOverlay: CopiedOverlay[] = overlayData.map((oi: OverlayInterface) => ({
      scheduleId: copiedSchedule.screenId,
      templateId: oi.templateId,
      position: oi.position,

    }))
    setCopiedOverlay(copiedOverlay);

    setIsScheduleSelected(true);
  };

  const handleApplySchedule = async () => {
    setShowModal(false);

    if (selectedScheduleFromModal) {
      const updatedSchedule = { ...selectedScheduleFromModal, screenId: props.screen?.id };

      const isValid = validateSchedule(updatedSchedule);

      if (isValid) {
        try {
          const data = await ApiHelper.post<ScheduleInterface[]>("/schedules", [updatedSchedule]) || [];

          if (data.length > 0) {
            const scheduleId = data[0].id;
            if (copiedScheduleTimes.length > 0) {
              const times = copiedScheduleTimes.map((time) => ({ ...time, scheduleId }));
              await ApiHelper.post("/scheduleTimes", times);
            }

            if (copiedSchedulePlaylist.length > 0) {
              const playlists = copiedSchedulePlaylist.map((playlist) => ({ ...playlist, scheduleId }));
              await ApiHelper.post("/schedulePlaylists", playlists);
            }

            if (copiedOverlay.length > 0) {
              const playlists = copiedOverlay.map((playlist) => ({ ...playlist, scheduleId }));
              await ApiHelper.post("/overlays", playlists);
            }

            await loadData();

            setSelectedSchedule(data[0]);
          }
        } catch {
          // Error applying schedule
        }
      }
    }
  };


  const validateSchedule = (schedule: ScheduleInterface) => {
    let errors: string[] = [];
    if (!schedule.name) errors.push("Please enter a schedule name.");
    return errors.length === 0;
  };

  const handleChangeInModal = (e: React.ChangeEvent<HTMLInputElement>) => {
    const updatedSchedule = { ...selectedScheduleFromModal, [e.currentTarget.id]: e.currentTarget.value };
    setSelectedScheduleFromModal(updatedSchedule);
  };



  const handleAdd = (e: React.MouseEvent) => {
    e.preventDefault();
    setSelectedSchedule({ screenId: props.screen?.id });
  }

  const getAddLink = () => {
    return (
        <Dropdown>
          <Dropdown.Toggle variant="success" id="dropdown-basic" >
            Add Schedule
          </Dropdown.Toggle>
          <Dropdown.Menu >
            <Dropdown.Item eventKey="Create new schedule" onClick={handleAdd}> <i className="fas fa-plus"></i> Create
              New Schedule
            </Dropdown.Item>
            <Dropdown.Item eventKey="Copy from another screen" onClick={handleCopyFromScreen}>
              <i className="fas fa-copy"></i> Copy From Another Screen
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
    );
  };

  const renderScreenList = () => (
      <ul className="screen-list">
        {allScreens.map(screen => (
            <li key={screen.id} className="screen-list-item">
              <div
                  onClick={() => handleScreenSelect(screen.id)}
                  className="screen-item"
              >
                {screen.name || `(Unnamed Screen - ID: ${screen.id})`}
              </div>
            </li>
        ))}
      </ul>
  );


  const renderScheduleList = () => (
      <Form>
        {selectedScreenSchedules.map(schedule => (
            <Form.Check
                type="radio"
                name="schedule"
                key={schedule.id}
                label={schedule.name || `(No name - ID: ${schedule.id})`}
                onChange={() => handleScheduleSelect(schedule)}
                className="styled-radio-button"
            />
        ))}
      </Form>
  );

  const getPlaylistsNames = (scheduleId: number) => {
    const result: string[] = [];
    const sp = ArrayHelper.getAll(schedulePlaylists, "scheduleId", scheduleId);
    if (sp.length > 0) {
      const playlistIds = ArrayHelper.getIds(sp, "playlistId");
      const ps = ArrayHelper.getAllArray(playlists, "id", playlistIds)
      ps.forEach(p => result.push(p.name));
    }
    if (result.length === 0) result.push("None");

    return (<><br /><span className="playlist-names">{result.join(", ")}</span></>);
  }

  const getSchedules = () => {
    const result: React.ReactElement[] = []
    schedules.forEach(s => {
      result.push(<li key={s.id}>
        <a href="about:blank" onClick={(e) => { e.preventDefault(); setSelectedSchedule(s); }}>{s.name || "(no name)"}</a>
        {getPlaylistsNames(s.id)}
      </li>)
    })
    return result;
  }

  const handleScheduleUpdated = () => {
    setSelectedSchedule(null);
    init();
  }

  const getDetails = () => {
    if (selectedSchedule) {
      const st = scheduleTimes ? ArrayHelper.getAll(scheduleTimes, "scheduleId", selectedSchedule.id) : null;
      return (
          <ScheduleDetails
              screen={props.screen}
              schedule={selectedSchedule}
              scheduleTimes={st}
              updatedCallback={handleScheduleUpdated}
          />
      );
    } else {
      return <></>;
    }
  };

  React.useEffect(init, [props.screen?.id]) // eslint-disable-line

  if (schedules === null) return <Loading />
  else return (
    <>
      <DisplayBox headerIcon="far fa-clock" headerText="Schedules" editContent={getAddLink()} >
        <ul>
          {getSchedules()}
        </ul>
        <SchedulesOverview schedules={schedules} scheduleTimes={scheduleTimes} />
      </DisplayBox>
      {getDetails()}

      <Modal
          show={showModal}
          onHide={() => {
            setShowModal(false);
            setIsScheduleSelected(false);
          }}
          centered
          size="lg"
          dialogClassName="custom-modal"
          onShow={resetModalState}
      >
        <Modal.Header closeButton>
          <div className="d-flex align-items-center">
            {selectedScreenSchedules.length > 0 && (
                <Button
                    variant="link"
                    onClick={() => {
                      setSelectedScreenSchedules([]);
                      setIsScheduleSelected(false);
                    }}
                    className="back-button"
                >
                  <i className="fas fa-arrow-left"></i>
                </Button>
            )}

            <Modal.Title>
              {isScheduleSelected
                  ? 'Selected Schedule'
                  : selectedScreenSchedules.length > 0
                      ? 'Select a Schedule'
                      : 'Select a Screen'}
            </Modal.Title>

          </div>
        </Modal.Header>

        <Modal.Body>
          {isScheduleSelected ? (
              <div>
                <Row>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <FormLabel>Schedule Name</FormLabel>
                      <FormControl
                          id="name"
                          value={selectedScheduleFromModal?.name}
                          onChange={handleChangeInModal}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <SchedulesOverview schedules={schedules} scheduleTimes={copiedScheduleTimes} />

              </div>
          ) : (
              selectedScreenSchedules.length > 0 ? renderScheduleList() : renderScreenList()
          )}
        </Modal.Body>



        <Modal.Footer className="d-flex justify-content-between">
          <Button
              variant="secondary"
              onClick={() => {
                setShowModal(false);
                setIsScheduleSelected(false);
              }}
          >
            Cancel
          </Button>


          {isScheduleSelected && (
              <Button
                  variant="primary"
                  onClick={handleApplySchedule}
              >
                Apply Schedule
              </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};